import React, { useMemo, useState } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { MoreVertical, Plus, Loader } from "lucide-react";
import { toast } from "react-hot-toast";
import Dropdown from "../../../components/ui/Dropdown";
import {
  useProductUpdates,
  useCreateProductUpdate,
  useUpdateProductUpdate,
  useDeleteProductUpdate,
  useSendTestProductUpdate,
  useSendProductUpdate,
  usePauseProductUpdate,
  useResumeProductUpdate,
  useScheduleProductUpdate,
  useUnscheduleProductUpdate,
} from "../hooks/useProductUpdates";
import ProductUpdateFormModal from "./ProductUpdateFormModal";
import ProductUpdateReportModal from "./ProductUpdateReportModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import ScheduleModal from "./ScheduleModal";

const statusStyles = {
  DRAFT: "bg-gray-100 text-gray-800",
  SCHEDULED: "bg-indigo-100 text-indigo-800",
  SENDING: "bg-blue-100 text-blue-800",
  PAUSED: "bg-yellow-100 text-yellow-800",
  SENT: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
};

const ProductUpdatesTable = () => {
  const { data, isLoading, isError, error } = useProductUpdates();
  const { mutate: createUpdate, isPending: isCreating } = useCreateProductUpdate();
  const { mutate: editUpdate, isPending: isEditing } = useUpdateProductUpdate();
  const { mutate: deleteUpdate, isPending: isDeleting } = useDeleteProductUpdate();
  const { mutate: sendTest } = useSendTestProductUpdate();
  const { mutate: send, isPending: isSending } = useSendProductUpdate();
  const { mutate: pause } = usePauseProductUpdate();
  const { mutate: resume } = useResumeProductUpdate();
  const { mutate: schedule, isPending: isScheduling } = useScheduleProductUpdate();
  const { mutate: unschedule } = useUnscheduleProductUpdate();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);

  const records = useMemo(() => data?.data || [], [data]);

  const handleCreate = () => {
    setEditingCampaign(null);
    setIsFormOpen(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (payload, action, testEmail) => {
    if (action === "sendTest") {
      if (!editingCampaign) {
        // Save the draft first so there's something to test-send.
        createUpdate(payload, {
          onSuccess: (res) => {
            const uuid = res?.data?.uuid;
            setEditingCampaign(res?.data);
            if (uuid) {
              sendTest(
                { uuid, testEmail },
                {
                  onSuccess: () => toast.success("Test email sent"),
                  onError: (err) => toast.error(err?.response?.data?.message || "Failed to send test"),
                }
              );
            }
          },
          onError: (err) => toast.error(err?.response?.data?.message || "Failed to save draft"),
        });
        return;
      }
      editUpdate(
        { uuid: editingCampaign.uuid, payload },
        {
          onSuccess: () => {
            sendTest(
              { uuid: editingCampaign.uuid, testEmail },
              {
                onSuccess: () => toast.success("Test email sent"),
                onError: (err) => toast.error(err?.response?.data?.message || "Failed to send test"),
              }
            );
          },
        }
      );
      return;
    }

    if (action === "send") {
      // Persist any unsaved edits first, then start the real send - the
      // draft the recipients get should always be what's currently in the
      // form, not whatever was last saved.
      editUpdate(
        { uuid: editingCampaign.uuid, payload },
        {
          onSuccess: () => {
            send(editingCampaign.uuid, {
              onSuccess: () => {
                toast.success("Sending started");
                setIsFormOpen(false);
              },
              onError: (err) => toast.error(err?.response?.data?.message || "Failed to start send"),
            });
          },
          onError: (err) => toast.error(err?.response?.data?.message || "Failed to save"),
        }
      );
      return;
    }

    if (editingCampaign) {
      editUpdate(
        { uuid: editingCampaign.uuid, payload },
        {
          onSuccess: () => {
            toast.success("Product update saved");
            setIsFormOpen(false);
          },
          onError: (err) => toast.error(err?.response?.data?.message || "Failed to save"),
        }
      );
    } else {
      createUpdate(payload, {
        onSuccess: () => {
          toast.success("Product update saved as draft");
          setIsFormOpen(false);
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to save"),
      });
    }
  };

  const handleSend = (campaign) => {
    send(campaign.uuid, {
      onSuccess: () => toast.success("Sending started"),
      onError: (err) => toast.error(err?.response?.data?.message || "Failed to start send"),
    });
  };

  const handlePause = (campaign) => {
    pause(campaign.uuid, {
      onSuccess: () => toast.success("Paused"),
      onError: (err) => toast.error(err?.response?.data?.message || "Failed to pause"),
    });
  };

  const handleScheduleConfirm = (scheduledAt) => {
    if (!scheduleTarget) return;
    schedule(
      { uuid: scheduleTarget.uuid, scheduledAt },
      {
        onSuccess: () => {
          toast.success("Product update scheduled");
          setScheduleTarget(null);
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to schedule"),
      }
    );
  };

  const handleUnschedule = (campaign) => {
    unschedule(campaign.uuid, {
      onSuccess: () => toast.success("Schedule removed, it is a draft again"),
      onError: (err) => toast.error(err?.response?.data?.message || "Failed to unschedule"),
    });
  };

  const handleResume = (campaign) => {
    resume(campaign.uuid, {
      onSuccess: () => toast.success("Resumed"),
      onError: (err) => toast.error(err?.response?.data?.message || "Failed to resume"),
    });
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteUpdate(deleteTarget.uuid, {
      onSuccess: () => {
        toast.success("Deleted");
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to delete");
        setDeleteTarget(null);
      },
    });
  };

  const getDropdownItems = (campaign) => {
    const items = [{ label: "View Report", onClick: () => setReportTarget(campaign.uuid) }];
    switch (campaign.status) {
      case "DRAFT":
      case "FAILED":
        items.push(
          { label: "Edit", onClick: () => handleEdit(campaign) },
          { label: "Send now", onClick: () => handleSend(campaign), className: "text-emerald-600" },
          ...(campaign.status === "DRAFT"
            ? [{ label: "Schedule", onClick: () => setScheduleTarget(campaign) }]
            : []),
          { label: "Delete", onClick: () => setDeleteTarget(campaign), className: "text-red-600" }
        );
        break;
      case "SCHEDULED":
        items.push(
          { label: "Reschedule", onClick: () => setScheduleTarget(campaign) },
          { label: "Unschedule", onClick: () => handleUnschedule(campaign), className: "text-yellow-600" },
          { label: "Send now", onClick: () => handleSend(campaign), className: "text-emerald-600" },
          { label: "Delete", onClick: () => setDeleteTarget(campaign), className: "text-red-600" }
        );
        break;
      case "SENDING":
        items.push({ label: "Pause", onClick: () => handlePause(campaign), className: "text-yellow-600" });
        break;
      case "PAUSED":
        items.push(
          { label: "Resume", onClick: () => handleResume(campaign), className: "text-emerald-600" },
          { label: "Delete", onClick: () => setDeleteTarget(campaign), className: "text-red-600" }
        );
        break;
      case "SENT":
        items.push({ label: "Delete", onClick: () => setDeleteTarget(campaign), className: "text-red-600" });
        break;
      default:
        break;
    }
    return items;
  };

  const columns = [
    { accessorKey: "subject", header: "Subject" },
    { accessorKey: "senderName", header: "Sender" },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[status] || "bg-gray-100 text-gray-800"}`}>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    },
    {
      accessorKey: "scheduledAt",
      header: "Scheduled for",
      cell: (info) => {
        const value = info.getValue();
        if (!value || info.row.original.status !== "SCHEDULED") return "-";
        const date = new Date(value);
        return <span title={`${date.toISOString()} (UTC)`}>{date.toLocaleString()}</span>;
      },
    },
    {
      accessorKey: "sentAt",
      header: "Sent",
      cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "—"),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Dropdown trigger={<MoreVertical className="w-4 h-4" />} items={getDropdownItems(row.original)} />
      ),
    },
  ];

  const table = useReactTable({ data: records, columns, getCoreRowModel: getCoreRowModel() });

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex justify-end pb-5 bg-white">
        <button
          onClick={handleCreate}
          className="flex gap-2 items-center px-5 py-2.5 text-sm font-bold text-white bg-[#013941] rounded-xl transition-all hover:bg-[#012B36] active:scale-95"
        >
          <Plus className="w-4 h-4" /> New Product Update
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading product updates <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <p className="text-lg font-medium">No product updates yet</p>
                        <p className="text-sm">Click "New Product Update" to create one</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ProductUpdateFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        isLoading={isCreating || isEditing || isSending}
        campaign={editingCampaign}
      />

      <ProductUpdateReportModal
        isOpen={!!reportTarget}
        onClose={() => setReportTarget(null)}
        uuid={reportTarget}
      />

      <ScheduleModal
        isOpen={!!scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onConfirm={handleScheduleConfirm}
        isLoading={isScheduling}
        campaign={scheduleTarget}
      />

      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        campaignName={deleteTarget?.subject}
      />
    </div>
  );
};

export default ProductUpdatesTable;
